/**
 * @author Rejo Varghese
 * @description Agent Ledger related component
 */

import React, { useState } from 'react';
import { Table, Row, Col, Space, Modal, Tooltip, Button, message } from 'antd';
import { isValid, isValidArray, sortJsonObject } from '../../utils/utilities';
import { TransactonsFormPage } from '../transaction-form/transaction-form.component';
import { POST_PAID_USER, PRE_PAID_USER } from '../../utils/constant';
import { ExpandRowComponent } from './expand-row.component';
import useColumnSearch from '../../ui-components/table-column-search';
import { Printer } from '../custom-components/custom-component';
import { getAPIDateFormat, getLocalDateAndTime } from '../../utils/date.utils';
import * as XLSX from 'xlsx';
import './agent-ledger.style.scss';

const generateJsonToExport = (title, dateRange, dataSource, hasReceived) => {

    let reportsXmlData = [];
    let totals = {
        totalSeats: 0, totalAmount: 0, totalGST: 0, confirmedTotalCommission: 0,
        grossAmount: 0, cancelledTotalSeats: 0, cancelledTotalPenalty: 0,
        cancelledTotalCommission: 0, netAmount: 0, amountReceived: 0, balanceDue: 0
    };
    if (isValidArray(dataSource) && dataSource.length > 0) {
        dataSource.forEach((record, index) => {
            Object.keys(totals).forEach((key) => {
                totals[key] += record[key] || 0;
            });
            let row = [
                index + 1,
                record.name || '',
                record.branch || 'N/A',
                record.totalSeats || 0,
                record.totalAmount || 0,
                record.totalGST || 0,
                record.confirmedTotalCommission || 0,
                record.grossAmount || 0,
                record.cancelledTotalSeats || 0,
                record.cancelledTotalPenalty || 0,
                record.cancelledTotalCommission || 0,
                record.netAmount || 0,
                record.netAmount || 0
            ];
            if (hasReceived) {
                row.push(record.amountReceived || 0, record.balanceDue || 0);
            }
            reportsXmlData.push(row);
        });
    }

    let headerRow = [
        "S.No", "Agent", "Branch", "Total Seats", "Total Fare", "Total GST", "Total Comm",
        "Gross Amount", "Total Cancel Seats", "Cancel Penalty", "Penalty Comm", "Net Amount", "Turnover"
    ];
    let totalRow = [
        "Total", `${dataSource.length} Agents`, "-",
        totals.totalSeats, totals.totalAmount, totals.totalGST, totals.confirmedTotalCommission,
        totals.grossAmount, totals.cancelledTotalSeats, totals.cancelledTotalPenalty,
        totals.cancelledTotalCommission, totals.netAmount, totals.netAmount
    ];
    if (hasReceived) {
        headerRow.push("Amount Received", "Balance Due");
        totalRow.push(totals.amountReceived, totals.balanceDue);
    }

    let fromDate = isValidArray(dateRange) ? getAPIDateFormat(dateRange[0]) : '';
    let toDate = isValidArray(dateRange) ? getAPIDateFormat(dateRange[1]) : '';
    let generatedTime = getLocalDateAndTime(new Date());

    var worksheet = XLSX.utils.aoa_to_sheet([
        [], // A1
        [], // A2
        ["Report", "From Date", "To Date", "Generated day/time"],
        [`Agents Ledger -${title}`, fromDate, toDate, generatedTime],
        [],
        [],
        headerRow,
        ...reportsXmlData,
        totalRow
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Agents Ledger');
    XLSX.writeFile(workbook, `agents-ledger${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
};

export const AgentLedgerComponent = (props) => {
    const {
        ledgerData,
        dateRange
    } = props;

    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [selectedAgentForPayment, setSelectedAgentForPayment] = useState(null);
    const [expandedRowKey, setExpandedRowKey] = useState(null);

    const commonColumns = [
        {
            title: 'S.No',
            key: 'sn',
            width: 20,
            align: 'center',
            render: (_, __, index) => index + 1
        },
        {
            title: 'Agent',
            dataIndex: 'name',
            key: 'name',
            width: 60,
            ...useColumnSearch({ dataIndexExt: 'name' }),
        },
        {
            title: 'Branch',
            dataIndex: 'branch',
            key: 'branch',
            width: 40,
            render: (value) => value || 'N/A',
            filters: ledgerData ? 
                [...new Set([
                    ...(ledgerData.with_commission_postpaid?.tableData?.map(item => item.branch).filter(Boolean) || []),
                    ...(ledgerData.with_commission_prepaid?.tableData?.map(item => item.branch).filter(Boolean) || []),
                    ...(ledgerData.without_commission_other?.tableData?.map(item => item.branch).filter(Boolean) || [])
                ])].map(branch => ({ text: branch, value: branch })) : [],
            onFilter: (value, record) => record.branch === value,
        },
        {
            title: 'Total Seats',
            dataIndex: 'totalSeats',
            key: 'totalSeats',
            width: 20,
            align: 'center'
        },
        {
            title: 'Total Fare',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: 'Total GST',
            dataIndex: 'totalGST',
            key: 'totalGST',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: 'Total Comm',
            dataIndex: 'confirmedTotalCommission',
            key: 'confirmedTotalCommission',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: (
                <Tooltip title="Gross Amount = Total Fare + Total GST - Total Comm">
                    <span>Gross Amount</span>
                </Tooltip>
            ),
            dataIndex: 'grossAmount',
            key: 'grossAmount',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: 'Total Cancel Seats',
            dataIndex: 'cancelledTotalSeats',
            key: 'cancelledTotalSeats',
            width: 20,
            align: 'center'
        },
        {
            title: 'Cancel Penalty',
            dataIndex: 'cancelledTotalPenalty',
            key: 'cancelledTotalPenalty',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: 'Penalty Comm',
            dataIndex: 'cancelledTotalCommission',
            key: 'cancelledTotalCommission',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: (
                <Tooltip title="Net Amount = Gross Amount + Cancellation Penalty - Penalty Commission">
                    <span>Net Amount</span>
                </Tooltip>
            ),
            dataIndex: 'netAmount',
            key: 'netAmount',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: 'Turnover',
            dataIndex: 'netAmount',
            key: 'netAmount',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        }
    ]

    let postPaidColumns = [
        ...commonColumns,
        {
            title: 'Amount Received',
            dataIndex: 'amountReceived',
            key: 'amountReceived',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`
        },
        {
            title: (
                <Tooltip title="Balance Due = Net Amount - Amount Received">
                    <span>Balance Due</span>
                </Tooltip>
            ),
            dataIndex: 'balanceDue',
            key: 'balanceDue',
            width: 40,
            align: 'right',
            render: (value) => `₹${value?.toLocaleString()}`,
            filters: [
                { text: 'Paid', value: 'paid' },
                { text: 'Due', value: 'due' }
            ],
            onFilter: (value, record) => {
                const balanceDue = record.balanceDue || 0;
                if (value === 'paid') {
                    return balanceDue === 0;
                } else if (value === 'due') {
                    return balanceDue !== 0;
                }
                return true;
            }
        },
    ];

    

    const TransactionTable = (transactionProps) => {
        const {
            title,
            columns,
            dataSource, 
            summaryData, 
            showBalanceDue, 
            expandable,
            agentType
        } = transactionProps;

        // Define expandable configuration for the table
        const expandableConfig = expandable ? {
            expandedRowRender: (record) => (
                <ExpandRowComponent
                    dateRange={dateRange}
                    expandRecord={record}
                    agentType={agentType}
                    setIsPaymentModalVisible={setIsPaymentModalVisible}
                    setSelectedAgentForPayment={setSelectedAgentForPayment}
                />
            ),
            rowExpandable: (record) => true, // All rows are expandable
            // expandIcon: () => null, // Remove the expand icon
            expandRowByClick: true, // Enable row click to expand
            expandIconColumnIndex: -1, // Hide the expand column completely
            expandedRowKeys: [expandedRowKey], // Control which row is expanded
            onExpand: (expanded, record) => {
                if (expanded) {
                    setExpandedRowKey(record.key || record.id || record.name);
                } else {
                    setExpandedRowKey(null);
                }
            },
        } : undefined;

        // Function to calculate summary from filtered data
        const calculateSummary = (data) => {
            if (!data || data.length === 0) return {};

            return data.reduce((acc, record) => {
                acc.totalSeats = (acc.totalSeats || 0) + (record.totalSeats || 0);
                acc.totalAmount = (acc.totalAmount || 0) + (record.totalAmount || 0);
                acc.totalGST = (acc.totalGST || 0) + (record.totalGST || 0);
                acc.confirmedTotalCommission = (acc.confirmedTotalCommission || 0) + (record.confirmedTotalCommission || 0);
                acc.grossAmount = (acc.grossAmount || 0) + (record.grossAmount || 0);
                acc.cancelledTotalSeats = (acc.cancelledTotalSeats || 0) + (record.cancelledTotalSeats || 0);
                acc.cancelledTotalPenalty = (acc.cancelledTotalPenalty || 0) + (record.cancelledTotalPenalty || 0);
                acc.cancelledTotalCommission = (acc.cancelledTotalCommission || 0) + (record.cancelledTotalCommission || 0);
                acc.netAmount = (acc.netAmount || 0) + (record.netAmount || 0);
                acc.amountReceived = (acc.amountReceived || 0) + (record.amountReceived || 0);
                acc.balanceDue = (acc.balanceDue || 0) + (record.balanceDue || 0);
                return acc;
            }, {});
        };

        // Create summary row component
        const SummaryRow = ({ data }) => {
            const summary = calculateSummary(data);

            // Helper function to check if a column exists in the current table
            const hasColumn = (columnKey) => {
                return columns.some(col => col.key === columnKey || col.dataIndex === columnKey);
            };

            // Helper function to format value based on column type
            const formatSummaryValue = (value, columnKey) => {
                if (value === null || value === undefined) return '0';

                // Check if it's a currency column
                if (columnKey === 'totalAmount' || columnKey === 'totalGST' ||
                    columnKey === 'confirmedTotalCommission' || columnKey === 'grossAmount' ||
                    columnKey === 'cancelledTotalPenalty' || columnKey === 'cancelledTotalCommission' ||
                    columnKey === 'netAmount' || columnKey === 'amountReceived' || columnKey === 'balanceDue') {
                    return `₹${value.toLocaleString()}`;
                }

                // For numeric columns that don't need currency symbol
                if (columnKey === 'totalSeats' || columnKey === 'cancelledTotalSeats') {
                    return value.toLocaleString();
                }

                return value.toString();
            };

            let cellIndex = 0;

            return (
                <Table.Summary.Row style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={cellIndex++} colSpan={1}>
                        <strong>Total</strong>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={cellIndex++} colSpan={1}>
                        <strong>{data?.length || 0} Agents</strong>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={cellIndex++} colSpan={1}>
                        <strong>-</strong>
                    </Table.Summary.Cell>

                    {hasColumn('totalSeats') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="center">
                            <strong>{formatSummaryValue(summary.totalSeats || 0, 'totalSeats')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('totalAmount') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.totalAmount || 0, 'totalAmount')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('totalGST') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.totalGST || 0, 'totalGST')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('confirmedTotalCommission') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.confirmedTotalCommission || 0, 'confirmedTotalCommission')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('grossAmount') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.grossAmount || 0, 'grossAmount')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('cancelledTotalSeats') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="center">
                            <strong>{formatSummaryValue(summary.cancelledTotalSeats || 0, 'cancelledTotalSeats')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('cancelledTotalPenalty') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.cancelledTotalPenalty || 0, 'cancelledTotalPenalty')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('cancelledTotalCommission') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.cancelledTotalCommission || 0, 'cancelledTotalCommission')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('netAmount') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.netAmount || 0, 'netAmount')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {hasColumn('netAmount') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.netAmount || 0, 'netAmount')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {showBalanceDue && hasColumn('amountReceived') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.amountReceived || 0, 'amountReceived')}</strong>
                        </Table.Summary.Cell>
                    )}

                    {showBalanceDue && hasColumn('balanceDue') && (
                        <Table.Summary.Cell index={cellIndex++} colSpan={1} align="right">
                            <strong>{formatSummaryValue(summary.balanceDue || 0, 'balanceDue')}</strong>
                        </Table.Summary.Cell>
                    )}
                </Table.Summary.Row>
            );
        };

        // Whether this table variant carries the post-paid payment columns.
        const hasReceived = columns.some((col) => col.dataIndex === 'amountReceived');

        // Read-only columns for the print view (no search/filters/tooltips).
        const printCurrency = (value) => `₹${value?.toLocaleString()}`;
        let printColumns = [
            { title: 'S.No', key: 'sn', render: (_, __, index) => index + 1 },
            { title: 'Agent', dataIndex: 'name', key: 'name' },
            { title: 'Branch', dataIndex: 'branch', key: 'branch', render: (value) => value || 'N/A' },
            { title: 'Total Seats', dataIndex: 'totalSeats', key: 'totalSeats', align: 'center' },
            { title: 'Total Fare', dataIndex: 'totalAmount', key: 'totalAmount', align: 'right', render: printCurrency },
            { title: 'Total GST', dataIndex: 'totalGST', key: 'totalGST', align: 'right', render: printCurrency },
            { title: 'Total Comm', dataIndex: 'confirmedTotalCommission', key: 'confirmedTotalCommission', align: 'right', render: printCurrency },
            { title: 'Gross Amount', dataIndex: 'grossAmount', key: 'grossAmount', align: 'right', render: printCurrency },
            { title: 'Total Cancel Seats', dataIndex: 'cancelledTotalSeats', key: 'cancelledTotalSeats', align: 'center' },
            { title: 'Cancel Penalty', dataIndex: 'cancelledTotalPenalty', key: 'cancelledTotalPenalty', align: 'right', render: printCurrency },
            { title: 'Penalty Comm', dataIndex: 'cancelledTotalCommission', key: 'cancelledTotalCommission', align: 'right', render: printCurrency },
            { title: 'Net Amount', dataIndex: 'netAmount', key: 'netAmount', align: 'right', render: printCurrency },
            { title: 'Turnover', dataIndex: 'netAmount', key: 'netAmount', align: 'right', render: printCurrency }
        ];
        if (hasReceived) {
            printColumns.push(
                { title: 'Amount Received', dataIndex: 'amountReceived', key: 'amountReceived', align: 'right', render: printCurrency },
                { title: 'Balance Due', dataIndex: 'balanceDue', key: 'balanceDue', align: 'right', render: printCurrency }
            );
        }

        const onExport = () => {
            if (!isValidArray(dataSource) || dataSource.length === 0) {
                message.warning("No data to export");
                return;
            }
            generateJsonToExport(title, dateRange, dataSource, hasReceived);
        };

        return (
            <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                    <Col>
                        <span style={{ fontWeight: 'bold' }}>{title} | </span>
                        <span style={{ fontWeight: 'bold' }}>Total Amount Received: ₹{summaryData['totalAmountReceived']?.toLocaleString()}</span>
                        {showBalanceDue && <span style={{ fontWeight: 'bold' }}> | Total Balance Due: ₹{summaryData['totalBalanceDue']?.toLocaleString()}</span>}
                    </Col>
                    <Col>
                        <Space>
                            <Button onClick={onExport}>Export</Button>
                            <Printer
                                showComponent={false}
                                zoomValue={0.6}
                                renderComponent={() => (
                                    <div>
                                        <Row>
                                            <h3>
                                                <strong>Agents Ledger -{title}</strong>
                                                {isValidArray(dateRange) &&
                                                    ` | ${getAPIDateFormat(dateRange[0])} to ${getAPIDateFormat(dateRange[1])}`
                                                }
                                            </h3>
                                        </Row>
                                        <Table
                                            columns={printColumns}
                                            dataSource={dataSource}
                                            rowKey={(record) => record.key || record.id || record.name}
                                            pagination={false}
                                            summary={(pageData) => <SummaryRow data={pageData} />}
                                        />
                                    </div>
                                )}
                            />
                        </Space>
                    </Col>
                </Row>
                <div style={{ width: '100%' }}>
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        expandable={expandableConfig}
                        rowKey={(record) => record.key || record.id || record.name || Math.random().toString()}
                        // scroll={{ x: 'max-content' }}
                        summary={(pageData) => <SummaryRow data={pageData} />}
                        pagination={{
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            defaultPageSize: 100,
                            onChange: (page, pageSize) => {
                                // Create unique ID for this pagination instance
                                const paginationId = `${title.replace(/\s+/g, '')}pagination`;
                                // Focus back to the pagination element after 100ms delay
                                setTimeout(() => {
                                    const paginationElement = document.getElementById(paginationId);
                                    if (paginationElement) {
                                        paginationElement.focus();
                                    }
                                }, 10);
                            },
                            onShowSizeChange: (current, size) => {
                                // Create unique ID for this pagination instance
                                const paginationId = `${title.replace(/\s+/g, '')}pagination`;
                                // Focus back to the pagination element after 100ms delay
                                setTimeout(() => {
                                    const paginationElement = document.getElementById(paginationId);
                                    if (paginationElement) {
                                        paginationElement.focus();
                                    }
                                }, 10);
                            }
                        }}
                    />
                    {/* Hidden div with unique ID for pagination focus */}
                    <div
                        id={`${title.replace(/\s+/g, '')}pagination`}
                        tabIndex={0}
                        style={{
                            top: '0',
                            left: '0',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                            opacity: 0,
                            pointerEvents: 'none'
                        }}
                        aria-hidden="true"
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="agent-ledger-component">
            {isValid(ledgerData['with_commission_postpaid']) && isValid(ledgerData['with_commission_postpaid']['tableData']) && ledgerData['with_commission_postpaid']['tableData'].length > 0 && (
                <TransactionTable
                    title=" With Commission Post Paid"
                    columns={postPaidColumns} 
                    dataSource={sortJsonObject(ledgerData['with_commission_postpaid']['tableData'], 'name', false)}
                    summaryData={ledgerData['with_commission_postpaid']['summary']} 
                    showBalanceDue={true}
                    expandable={true}
                    agentType={POST_PAID_USER}
                />
            )}
            {isValid(ledgerData['without_commission_postpaid']) && isValid(ledgerData['without_commission_postpaid']['tableData']) && ledgerData['without_commission_postpaid']['tableData'].length > 0 && (
                <TransactionTable
                    title=" Without Commission Post Paid"
                    columns={postPaidColumns}
                    dataSource={sortJsonObject(ledgerData['without_commission_postpaid']['tableData'], 'name', false)}
                    summaryData={ledgerData['without_commission_postpaid']['summary']}
                    showBalanceDue={true}
                    expandable={true}
                    agentType={POST_PAID_USER}
                />
            )}
            {isValid(ledgerData['with_commission_prepaid']) && isValid(ledgerData['with_commission_prepaid']['tableData']) && ledgerData['with_commission_prepaid']['tableData'].length > 0 && (
                <TransactionTable
                    title=" With Commission Pre Paid"
                    columns={commonColumns}
                    dataSource={sortJsonObject(ledgerData['with_commission_prepaid']['tableData'], 'name', false)}
                    summaryData={ledgerData['with_commission_prepaid']['summary']}
                    showBalanceDue={true}
                    expandable={false}
                    agentType={PRE_PAID_USER}
                />
            )}
            {isValid(ledgerData['without_commission_prepaid']) && isValid(ledgerData['without_commission_prepaid']['tableData']) && ledgerData['without_commission_prepaid']['tableData'].length > 0 && (
                <TransactionTable
                    title=" Without Commission Pre Paid"
                    columns={commonColumns}
                    dataSource={sortJsonObject(ledgerData['without_commission_prepaid']['tableData'], 'name', false)}
                    summaryData={ledgerData['without_commission_prepaid']['summary']}
                    showBalanceDue={true}
                    expandable={false}
                    agentType={PRE_PAID_USER}
                />
            )}
            {isValid(ledgerData['with_commission_other']) && isValid(ledgerData['with_commission_other']['tableData']) && ledgerData['with_commission_other']['tableData'].length > 0 && (
                <TransactionTable
                    title=" With Commission"
                    columns={commonColumns}
                    dataSource={sortJsonObject(ledgerData['with_commission_other']['tableData'], 'name', false)}
                    summaryData={ledgerData['with_commission_other']['summary']}
                    showBalanceDue={true}
                    expandable={false}
                    agentType={null}
                />
            )}
            {isValid(ledgerData['without_commission_other']) && isValid(ledgerData['without_commission_other']['tableData']) && ledgerData['without_commission_other']['tableData'].length > 0 && (
                <TransactionTable
                    title=" Without Commission"
                    columns={commonColumns}
                    dataSource={sortJsonObject(ledgerData['without_commission_other']['tableData'], 'name', false)}
                    summaryData={ledgerData['without_commission_other']['summary']}
                    showBalanceDue={false}
                    expandable={false}
                    agentType={null}
                />
            )}

            {/* Agent Payment Modal */}
            <Modal
                title={`Agent Payment - ${selectedAgentForPayment?.name || ''}`}
                visible={isPaymentModalVisible}
                onCancel={() => {
                    setIsPaymentModalVisible(false);
                    // setSelectedAgentForPayment(null);
                }}
                footer={null}
                width={1200}
                destroyOnClose={true}
            >
                <TransactonsFormPage
                    showTransactionForm={true}
                    setShowTransactionForm={() => {
                    }}
                    agents={[selectedAgentForPayment]}
                    selectedAgentId={selectedAgentForPayment?.id}
                    onCompleteTransaction={() => {
                        setIsPaymentModalVisible(false);
                    }}
                    transactionSummaryData={selectedAgentForPayment?.transactionSummaryData}
                />
            </Modal>
        </div>
    )
};